import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Users, Pencil, Trash2 } from "lucide-react";
import { posts } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import UrgencyBadge from "../components/common/UrgencyBadge";
import StatusBadge from "../components/common/StatusBadge";
import UserChip from "../components/common/UserChip";
import CpBadge from "../components/social/CpBadge";
import ResponseCard from "../components/posts/ResponseCard";
import Markdown from "../components/common/Markdown";
import LoadingOverlay from "../components/LoadingOverlay";
import { CATEGORY_LABELS } from "../lib/constants";

export default function PostDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responding, setResponding] = useState(false);

  const fetchPost = useCallback(async () => {
    try {
      const [postData, responsesData] = await Promise.all([
        posts.get(id),
        posts.getResponses(id, user.id),
      ]);
      setPost(postData);
      setResponses(responsesData.responses || []);
    } catch (err) {
      setError("Failed to load post");
    } finally {
      setLoading(false);
    }
  }, [id, user.id, location.key]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const handleRespond = async () => {
    setResponding(true);
    try {
      await posts.respond(id, user.id);
      fetchPost();
    } catch (err) {
      if (err.status === 409) alert("You already responded to this post.");
      else alert(err.error || "Failed to respond");
    } finally {
      setResponding(false);
    }
  };

  const handleAccept = async (responseId) => {
    await posts.updateResponse(id, responseId, { author_id: user.id, status: "accepted" });
    fetchPost();
  };

  const handleReject = async (responseId) => {
    await posts.updateResponse(id, responseId, { author_id: user.id, status: "rejected" });
    fetchPost();
  };

  const handleComplete = async (responseId) => {
    await posts.completeResponse(id, responseId, user.id);
    fetchPost();
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    await posts.delete(id, user.id);
    navigate("/activity");
  };

  if (loading) return <LoadingOverlay />;
  if (error) return <p className="text-coral py-8">{error}</p>;
  if (!post) return null;

  const isAuthor = user.id === (post.author?.id || post.author_id);
  const myResponse = responses.find((r) => r.responder?.id === user.id || r.responder_id === user.id);
  const canRespond = !isAuthor && !myResponse && ["open", "partially_filled"].includes(post.status);
  const dateRange = post.needed_by_end ? `${post.needed_by_start} - ${post.needed_by_end}` : post.needed_by_start;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-walnut-light hover:text-walnut">
          <ArrowLeft size={16} /> Back
        </button>
        {isAuthor && (
          <div className="flex gap-2">
            <Link
              to={`/posts/${id}/edit`}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-walnut-light border border-stone rounded-md hover:bg-cream"
            >
              <Pencil size={14} /> Edit
            </Link>
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-coral border border-coral/30 rounded-md hover:bg-coral/10"
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-2">
        <UrgencyBadge urgency={post.urgency} />
        <StatusBadge status={post.status} />
        <span className="text-sm text-walnut-light">{CATEGORY_LABELS[post.category]}</span>
        <span className="text-sm text-walnut-light/60 capitalize">{post.type}</span>
      </div>

      {post.author && (
        <div className="mb-4">
          <UserChip user={post.author} />
        </div>
      )}

      <Markdown content={post.description} className="text-walnut leading-relaxed mb-6" />

      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-walnut-light mb-6">
        <span className="inline-flex items-center gap-1">
          <Calendar size={14} /> {dateRange}
        </span>
        {post.takers_needed != null && (
          <span className="inline-flex items-center gap-1">
            <Users size={14} /> {post.takers_filled} of {post.takers_needed} spots filled
          </span>
        )}
        <CpBadge min={post.cp_estimate_min} max={post.cp_estimate_max} />
      </div>

      {canRespond && (
        <button
          onClick={handleRespond}
          disabled={responding}
          className="w-full md:w-auto px-6 py-3 bg-terracotta text-white rounded-md font-medium hover:bg-terracotta-dark transition-colors disabled:opacity-50 mb-6"
        >
          {responding ? "Responding..." : post.type === "request" ? "I Want to Help" : "I'm Interested"}
        </button>
      )}

      {myResponse && !isAuthor && (
        <div className="p-4 bg-cream rounded-lg mb-6">
          <p className="text-sm text-walnut">
            Your response: <span className="font-medium capitalize">{myResponse.status}</span>
          </p>
          {myResponse.status === "accepted" && !myResponse.completed_at && (
            <Link to={`/chat/${myResponse.id}`} className="inline-block mt-2 px-4 py-2 bg-terracotta text-white rounded-md text-sm font-medium hover:bg-terracotta-dark transition-colors">
              Open Chat
            </Link>
          )}
          {myResponse.completed_at && (
            <Link to={`/rate/${myResponse.id}`} className="inline-block mt-2 px-4 py-2 bg-terracotta text-white rounded-md text-sm font-medium hover:bg-terracotta-dark transition-colors">
              Leave a Review
            </Link>
          )}
        </div>
      )}

      {responses.length > 0 && (
        <div>
          <h2 className="font-display text-lg font-semibold text-walnut mb-3">
            Responses ({responses.length})
          </h2>
          <div className="space-y-2">
            {responses.map((r) => (
              <ResponseCard
                key={r.id}
                response={r}
                postAuthorId={post.author?.id || post.author_id}
                currentUserId={user.id}
                postId={id}
                onAccept={handleAccept}
                onReject={handleReject}
                onComplete={handleComplete}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
